import React, { type FC } from 'react'

import { useRecoilCallback, useRecoilValue } from 'recoil'

import { useIntl } from 'react-intl'

import {
  DataListHeader,
  DataListRow,
  Flex,
  GRID_VARIANT_SMALL,
  Grid,
  GridCell,
  LINK_VARIANT_DEFAULT,
  Link,
  PaginationBar,
  SKELETON_LOADER_VARIANT_ONWHITE,
  SPACING_8,
  SkeletonLoader,
  SpacingVertical,
  TEXT_VARIANT_ITALIC_LIGHT,
  TEXT_VARIANT_LIGHT,
  TITLE_VARIANT_S,
  Text,
  Title,
  TruncateAndTooltip,
} from 'mailup-web-components'

import { evalCondition } from 'mailup-react-helpers'

import { openRecipientsListInGroupNewTab } from 'Helpers/recipient'

import { modalRecipientGroupsFetchGroupsCallback } from 'Pages/RecipientsList/recoil/callbacks/modalRecipientGroups'
import { modalRecipientGroupsState } from 'Pages/RecipientsList/recoil/state/modalRecipientGroups'

const MySkel: FC = () => (
  <SkeletonLoader
    variant={SKELETON_LOADER_VARIANT_ONWHITE}
    height="15px"
    style={{ marginBottom: '4.5px' }}
  />
)

const MemoSkel = React.memo(MySkel)

const GroupsList: FC = () => {
  const intl = useIntl()

  const {
    status,
    items,
    totalItems,
    orderBy,
    orderDescending,
    pageNumber,
    loading,
    pageSize,
  } = useRecoilValue(modalRecipientGroupsState)

  const fetchGroups = useRecoilCallback(
    modalRecipientGroupsFetchGroupsCallback,
    [],
  )

  if (status === 'ready' && totalItems === 0) {
    return (
      <Flex flexDirection="column" gap="16px">
        <Title variant={TITLE_VARIANT_S}>
          {intl.formatMessage({ id: 'Load.RecipientsList.GroupsMembership' })}
        </Title>
        <Text variant={TEXT_VARIANT_ITALIC_LIGHT}>
          {intl.formatMessage({
            id: 'Load.RecipientsList.GroupsMembershipNoGroups',
          })}
        </Text>
      </Flex>
    )
  }

  const onClickSort = (
    fieldName: 'IdGroup' | 'GroupName' | 'AssignmentDate',
  ): void => {
    void fetchGroups({ orderBy: fieldName })
  }

  const handleGroupClick = (e: React.MouseEvent<HTMLLinkElement>): void => {
    const idGroup = e.currentTarget.dataset.value

    if (idGroup !== undefined) {
      openRecipientsListInGroupNewTab(idGroup)
    }
  }

  const handleChangePage = (pageNumber: number): void => {
    void fetchGroups({
      pageNumber,
    })
  }

  const handleChangePageSize = (pageSize: number): void => {
    void fetchGroups({
      pageNumber: 1,
      pageSize,
    })
  }

  const hasAdvRecMgmt: boolean = evalCondition('co:ADVRECIPIENTMGMT')

  const heads = [
    {
      label: intl.formatMessage({
        id: 'Load.MailupCommon.Id',
      }),
      span: 2,
      sortable: true,
      field: 'IdGroup',
    },
    {
      label: intl.formatMessage({
        id: 'Load.Common.Group',
      }),
      span: hasAdvRecMgmt ? 7 : 10,
      sortable: true,
      field: 'GroupName',
    },
    hasAdvRecMgmt
      ? {
          label: intl.formatMessage({
            id: 'Load.RecipientsList.GroupAssignmentDate',
          }),
          span: 3,
          sortable: true,
          field: 'AssignmentDate',
        }
      : undefined,
  ].filter((o) => o !== undefined)

  return (
    <div>
      <SpacingVertical variant={SPACING_8} />
      <DataListHeader
        heads={heads}
        orderByStatus={orderBy}
        orderDescendingStatus={orderDescending}
        handleChangeColumnsOrderCallback={onClickSort}
        cols={12}
        gridVariant={GRID_VARIANT_SMALL}
      />
      <section>
        {loading &&
          Array.from({
            length: items.length > 0 ? items.length : pageSize,
          }).map((_, idx) => (
            <DataListRow key={idx}>
              <Grid cols={12} variant={GRID_VARIANT_SMALL}>
                <GridCell span={heads[0]?.span}>
                  <MemoSkel />
                </GridCell>
                <GridCell span={heads[1]?.span}>
                  <MemoSkel />
                </GridCell>
                {hasAdvRecMgmt && (
                  <GridCell span={heads[2]?.span}>
                    <MemoSkel />
                  </GridCell>
                )}
              </Grid>
            </DataListRow>
          ))}
        {!loading &&
          items.map((item) => (
            <DataListRow key={item.Id}>
              <Grid cols={12} variant={GRID_VARIANT_SMALL}>
                <GridCell span={heads[0]?.span}>
                  <Text variant={TEXT_VARIANT_LIGHT}>{item.Id}</Text>
                </GridCell>
                <GridCell span={heads[1]?.span}>
                  <Link
                    variant={LINK_VARIANT_DEFAULT}
                    data-mu-test="group-name"
                    data-value={item.Id}
                    onClick={handleGroupClick}
                  >
                    <TruncateAndTooltip text={item.Name} />
                  </Link>
                </GridCell>
                {hasAdvRecMgmt && (
                  <GridCell span={heads[2]?.span}>
                    <Text variant={TEXT_VARIANT_LIGHT}>
                      {item.AssignmentDate}
                    </Text>
                  </GridCell>
                )}
              </Grid>
            </DataListRow>
          ))}
      </section>

      <PaginationBar
        margin="24px 0 0 0"
        minPageSize={5}
        pageNumber={pageNumber}
        pageSize={pageSize}
        totalItems={totalItems}
        handleChangePage={handleChangePage}
        handleChangePageSize={handleChangePageSize}
        strings={{
          of: intl.formatMessage({ id: 'Load.Pager.Of' }),
          label: intl.formatMessage({ id: 'Load.Pager.Items' }),
        }}
        data-mu-test="recipient-groups-list__pagination"
        formatValues={intl.formatNumber}
        sizeSelectOpenUp
      />
    </div>
  )
}

export default GroupsList
